/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Button2 from "../../../shared/components/button/Button2";
import InputFieldComponent from "../../../shared/components/forms/input-field/input-field";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { FaExclamationTriangle } from "react-icons/fa";
import axios, { AxiosResponse } from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { ErroObj } from "../../../shared/interfaces/model/errorObj.interface";
import apiClient from "../../../store/shared/api/apiClient";
import { ApiErrorHandler } from "../../../../errorHandler";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const activationUrl = process.env.REACT_APP_ACTIVATION_URL;

const SuccessPopup: React.FC<{ message: string; onClose: () => void }> = ({
    message,
    onClose,
}) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="relative bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4M6 12a9 9 0 1118 0 9 9 0 11-18 0z"
                    />
                </svg>
            </div>
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
                Success!
            </h2>
            <p className="text-center text-gray-600 mb-6">{message}</p>
            <div className="flex justify-center">
                <button
                    onClick={() => {
                        onClose(); // Chiude il popup
                        window.location.href = `https://${activationUrl}`;
                        //window.location.href = "https://nexum-saas-portal.oa.r.appspot.com/"; // Redirect alla URL specificata
                        // Da stotituire con window.location.href = "https://nexum-saas-portal.firebaseapp.com" per prod;
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-green-300 to-green-700 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-800 shadow-md transition-transform transform hover:scale-105"
                >
                    Close
                </button>
            </div>
        </div>
    </div>
);

interface IssuePopupProps {
    show: boolean;
    onClose: () => void;
    onConfirm: (title: string, description: string) => void;
    recaptchaValue: string | null;
}

const verifyRecaptchaAndRecover = async (body: any) => {
    const object = {
        token: body.recaptchaValue, // Token del reCAPTCHA
        recaptchaAction: body.recaptchaAction, // Azione specifica
        recaptcha: true,
        email: body.email,
    };

    const URL = `${baseUrl}/nexum-saas/recoverAccount`;

    return apiClient
        .post(URL, object)
        .then((response: AxiosResponse) => response)
        .catch((e: ErroObj) => {
            ApiErrorHandler(
                e.message,
                e.config.url,
                JSON.stringify(e.config.headers),
                e.config.method
            );
        });
};

const IssuePopup: React.FC<IssuePopupProps> = ({
    show,
    onClose,
    onConfirm,
    recaptchaValue,
}) => {
    const [title, setTitle] = useState("Request to recover email");
    const [description, setDescription] = useState("");
    // const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);

    useEffect(() => {
        if (show) {
            setDescription(""); // Reset description when popup is shown
        }
    }, [show]);

    const handleSubmitPopup = async () => {
        if (!recaptchaValue) {
            alert("Please complete the reCAPTCHA challenge.");
            return;
        }
        const payload = {
            recaptchaValue, // Token del reCAPTCHA
            recaptchaAction: "password_reset", // Azione specifica
            recaptcha: true,
        };

        onConfirm(title, description);
        onClose(); // Close popup after submission
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                <h2 className="text-xl font-semibold mb-4">Recover email</h2>
                <div className="flex items-center bg-green-100 p-4 rounded-lg mb-6">
                    <FaExclamationTriangle className="text-red-500 w-6 h-6 mr-3" />
                    <p className="text-sm text-black-800">
                        Make sure to provide as much information as possible in the
                        description (e.g. first name, last name, company name, VAT number,
                        etc.) to help us recover your account faster.
                    </p>
                </div>
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        rows={4}
                        placeholder="Enter issue description"
                    ></textarea>
                </div>
                <div className="flex justify-end space-x-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmitPopup}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-400"
                    >
                        Recover
                    </button>
                </div>
            </div>
        </div>
    );
};

const AccountRecovery: React.FC = () => {
    const [formType, setFormType] = useState<"email" | "password">("password");
    const [formData, setFormData] = useState({
        name: "",
        surname: "",
        company: "",
        vat: "",
        phone: "",
        email: "",
    });
    const [loading, setLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (!recaptchaValue) {
            alert("Please complete the reCAPTCHA challenge.");
            return;
        }

        const payload = {
            recaptchaValue, // Token del reCAPTCHA
            recaptchaAction: "password_reset", // Azione specifica
            recaptcha: true,
            email: formData.email,
        };
        // console.log("payload", payload)

        setLoading(true);
        try {
            const response = await verifyRecaptchaAndRecover(payload);

            if (response && response.data.message) {
                setSuccessMessage(response.data.message); // Imposta il messaggio di successo
                setShowSuccessPopup(true); // Mostra il popup
            } else {
                alert("Invalid response from server.");
            }

            // if (response.status === 200 && response.data.recaptchaValid) {
            //     alert("reCAPTCHA verified successfully! Proceeding with the recovery...");
            //     // Logica per il recupero email/password
            // } else {
            //     alert("Invalid reCAPTCHA. Please try again.");
            // }
        } catch (error) {
            // console.error("API error:", error);
            alert(
                "An error occurred while processing your request. Please try again later."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPopup = () => {
        setShowPopup(true);
    };

    const handleReportIssue = (title: string, description: string) => {
        const projectKey = process.env.REACT_APP_JIRA_PROJECT_KEY;
        const label = process.env.REACT_APP_JIRA_LABEL;

        const fullDescription = `Recover email: \nMessage: \n${description}`;

        const jiraData = {
            fields: {
                project: { key: projectKey },
                summary: title,
                description: {
                    type: "doc",
                    version: 1,
                    content: [
                        {
                            type: "paragraph",
                            content: [{ type: "text", text: fullDescription }],
                        },
                    ],
                },
                issuetype: { name: "Task" },
                labels: [label],
            },
        };

        axios.post(`${process.env.REACT_APP_API_BASE_URL}/nexum-saas/create-jira-issue`, jiraData)
            .then((response: AxiosResponse) => {
                alert("Ticket created successfully: " + response.data.key);
                setShowPopup(false);
            })
            .catch((error: any) => {
                alert("Failed to create Jira ticket: " + error.message); // Error handling
                console.error('Error creating Jira issue:', error);
            })
            .finally(() => {
                setLoading(false);
            });

        // const email = process.env.REACT_APP_JIRA_EMAIL ?? "s.orlando@nexum-ai.com";
        // const apiKey = process.env.REACT_APP_JIRA_MAIN_KEY ?? "";
        // const authHeader = `Basic ${btoa(email + ":" + apiKey)}`;

        // axios
        //     .post("/rest/api/3/issue", jiraData, {
        //         headers: {
        //             Authorization: authHeader,
        //             Accept: "application/json",
        //             "Content-Type": "application/json",
        //         },
        //     })
        //     .then((response: AxiosResponse) => {
        //         alert("Ticket created successfully: " + response.data.key); // Success message with Jira ticket key
        //         setShowPopup(false);
        //     })
        //     .catch((error: any) => {
        //         alert("Failed to create Jira ticket: " + error.message); // Error handling
        //     });
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
                {/* Back Icon */}
                <div className="flex items-center justify-between mb-6 cursor-pointer">
                    <Link
                        to="/"
                        className="text-gray-600 hover:text-gray-800 flex items-center"
                    >
                        <ArrowLeft className="w-6 h-6" /> Back
                    </Link>
                </div>
                <h1 className="text-xl font-bold text-center mb-6">
                    {formType === "email" ? "Recover Email" : "Recover Password"}
                </h1>

                {formType === "email" && (
                    <>
                        <motion.div
                            className="bg-gradient-to-r from-green-300 to-green-700 p-8 rounded-xl text-white"
                            whileHover={{ scale: 1.02 }}
                        >
                            <MessageSquare className="w-8 h-8 mb-4" />
                            <h3 className="text-xl font-semibold mb-2">
                                Request to recover your account email
                            </h3>
                            <p className="mb-4">Get instant help from our support team</p>
                            <button
                                onClick={handleOpenPopup}
                                className="bg-white text-[#0c1d46] py-2 px-6 rounded-lg hover:bg-green-600 transition-colors"
                            >
                                Recover
                            </button>
                        </motion.div>
                    </>
                )}

                {formType === "password" && (
                    <>
                        <InputFieldComponent
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChangeHandler={handleInputChange}
                            id="email"
                            type="email"
                            title="Email"
                        />
                        <Button2
                            title="Submit"
                            onClick={handleSubmit}
                            isLoading={loading}
                            disabled={loading}
                            className="w-full mt-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        />
                    </>
                )}

                <Link
                    to="#"
                    className="block text-center mt-4 text-blue-600 underline"
                    onClick={() =>
                        setFormType(formType === "email" ? "password" : "email")
                    }
                >
                    {formType === "email" ? "Recover Password?" : "Recover Email?"}
                </Link>
                <div className="mt-4">
                    <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ""}
                        onChange={(value: React.SetStateAction<string | null>) =>
                            setRecaptchaValue(value)
                        }
                    />
                </div>
            </div>

            {showPopup && (
                <IssuePopup
                    show={showPopup}
                    onClose={() => setShowPopup(false)}
                    onConfirm={handleReportIssue}
                    recaptchaValue={recaptchaValue}
                />
            )}
            {showSuccessPopup && (
                <SuccessPopup
                    message={successMessage}
                    onClose={() => setShowSuccessPopup(false)}
                />
            )}
        </div>
    );
};

export default AccountRecovery;
